import React, { useCallback, useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import Modal from '@ublique/components/LoadingModal'
import { difference, isEmpty, uniqueId } from 'lodash'
import 'pages/Administration/components/index.scss'
import Loading from '@ublique/components/Loading'

import {
  defaultAddFormState,
  blockPrefix,
  defaultBlock,
  scenarioFields,
  CREATE_ROW,
  UPDATE_ROW
} from 'pages/Administration/Tabs/Scenario/constant'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import {
  selectConfig,
  validateAddForm
} from 'pages/Administration/Tabs/Scenario/helpers'
import { savePermission } from 'pages/Administration/Tabs/Scenario/actions'
import { sendNotification } from 'pages/Administration/helpers'
import CreationForm from 'pages/Administration/Tabs/Scenario/ScenarioPanel/CreationForm'

const ScenarioModal = ({ open, close, data }) => {
  const { scenarioValue, roles } = data
  const { t } = useTranslation('Administration', 'Common')
  const [errors, setErrors] = useState({})
  const [values, setValues] = useState(scenarioValue || defaultAddFormState)
  const [blocksList, setBlocksList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [operationType, setOperationType] = useState(CREATE_ROW)

  const { config, additionalColumns } = useStoreSelector(
    'appConfig',
    selectConfig
  )

  const scenarioTypes = Object.keys(config)

  const [selectedTypes, remainingTypes] = useMemo(() => {
    const selected = Object.keys(values?.blocks)
      .reduce((acc, key) => [...acc, ...values.blocks[key].scenarioTypes], [])
      .map((item) => item.value)

    return [selected, difference(scenarioTypes, selected)]
  }, [values, config])

  const scenarioMemoFields = useMemo(
    () => [...scenarioFields, ...additionalColumns.map((col) => col.field)],
    [additionalColumns]
  )

  useEffect(() => {
    if (isEmpty(values.blocks)) {
      const id = uniqueId(blockPrefix)

      setBlocksList([id])
      setValues((prev) => ({
        ...prev,
        blocks: {
          [id]: {
            scenarioTypes: [],
            minePermissions: [],
            otherPermissions: []
          }
        }
      }))
    } else {
      setOperationType(UPDATE_ROW)
      setBlocksList(Object.keys(values.blocks))
    }
  }, [])

  const onChange = useCallback(
    (dataField) => {
      setValues((prev) => {
        const newValues = {
          ...prev,
          ...dataField
        }

        const { errors: formErrors } = validateAddForm(newValues)
        setErrors(formErrors)

        return newValues
      })
    },
    [setErrors, setValues]
  )

  const onBlockChange = (blockId) => (dataField) => {
    setValues((prevValues) => {
      const newValues = {
        ...prevValues,
        blocks: {
          ...prevValues.blocks,
          [blockId]: {
            ...prevValues.blocks[blockId],
            ...dataField
          }
        }
      }
      const { errors: formErrors } = validateAddForm(newValues)
      setErrors(formErrors)

      return newValues
    })
  }

  const onSubmit = async () => {
    const { isValid, errors: formErrors } = validateAddForm({
      ...values,
      selectedTypes
    })

    if (!isValid) {
      setErrors(formErrors)
      return
    }

    setIsLoading(true)

    try {
      await savePermission({ ...values, columns: scenarioMemoFields })
      sendNotification({ t, info: operationType, kind: 'success' })
    } catch (err) {
      console.error(err)
      setIsLoading(false)
      close()
    }

    close()
    setIsLoading(false)
  }

  const onAddBlock = () => {
    const id = uniqueId(blockPrefix)

    setBlocksList((prev) => [...prev, id])
    setValues((prev) => ({
      ...prev,
      blocks: {
        ...prev.blocks,
        [id]: { ...defaultBlock }
      }
    }))
  }

  const onRemoveBlock = (id) => {
    setBlocksList((list) => list.filter((blockId) => blockId !== id))
    setValues((prev) => {
      const { blocks, ...other } = prev
      const { [id]: remove, ...otherBlocks } = blocks

      const result = {
        ...other,
        blocks: otherBlocks
      }

      return result
    })
  }

  return (
    <Modal
      open={open}
      id='selectedRowsLength'
      preventCloseOnClickOutside
      className='scenario-creation-modal admin-view'
      modalHeading={t('ScenarioManagement.ScenarioPermissions')}
      primaryButtonText={t('Common:Save')}
      secondaryButtonText={t('Common:Cancel')}
      iconDescription={t('Common:Close')}
      primaryButtonDisabled={!isEmpty(errors)}
      onRequestSubmit={onSubmit}
      onRequestClose={close}
      onSecondarySubmit={close}
    >
        {isLoading && <Loading description='Loading' withOverlay />}
        <CreationForm
          t={t}
          values={values}
          errors={errors}
          onChange={onChange}
          roles={roles}
          blocksList={blocksList}
          remainingTypes={remainingTypes}
          selectedTypes={selectedTypes}
          scenarioFields={scenarioMemoFields}
          onAddBlock={onAddBlock}
          onBlockChange={onBlockChange}
          onRemoveBlock={onRemoveBlock}
        />
    </Modal>
  )
}

export default ScenarioModal
